import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowDown, ArrowUp } from 'lucide-react';
import {
  acceptTitleSuggestion, addDeck, addDeckVersion, confirmDeckOrder, dismissTitleSuggestion, downloadDecksZip,
  downloadMaterial, fetchMaterials, publishMaterial, removeDeck, reorderDecks, reviewMaterial, setDeckPresenters,
  setPresentsWithoutSlides, unpublishMaterial,
} from './ProgramAdminApi';
import ConfirmDialog from './ConfirmDialog';
import { formatDateTime } from './programAdminModel';
import { ConsentTag } from './ui';
import {
  ACCENT, NAVY,
  HistoryWhen, MiniButton, PanelHint, PanelLabel, PanelRow, PanelSection, PanelSectionTitle, PersonMeta,
  PersonName, SpotActions,
} from './programAdminStyles';

// The talk files of one program entry (design §4.7, plan §7.7): the speakers with what is still
// needed from them, the files in their confirmed order with presenters, versions, review and
// publication, the order's state and, for staff, every action a speaker can take (D5). It owns its
// own reads and writes through ./ProgramAdminApi (the ImportsArea pattern) and sits in the entry
// panel next to the entry's time, room and spots; it renders for a removed entry too, because what
// was uploaded and published stays worth reading.
//
// The staff API answers the talk's overview (MaterialViews.TalkOverview): speakers with their
// readiness state and consent, decks in order with every version and the blockers that stop a
// publish, the order and its confirmation, and the files nobody presents. Every write answers the
// overview again, so the panel re-reads it and never keeps its own copy of the server's rules. A 409
// on a reorder or a confirmation means the list changed under this screen: the panel says so and
// re-reads, so a stale screen can never misorder.

// A file size a human reads at a glance: bytes under 1 KB, then KB, then MB.
const formatSize = (bytes) => {
  if (typeof bytes !== 'number' || Number.isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// The published/consent tag tones (ConsentTag in ./ui).
const consentTone = (consent) => {
  if (consent === 'GRANTED') return 'granted';
  if (consent === 'DENIED') return 'denied';
  return 'unset';
};

const nonEmpty = (list) => (list || []).filter((value) => !!value);

const deckOf = (row) => row?.deck || {};
const positionOf = (row) => deckOf(row).position ?? 0;

// Decks in the order the speakers present them. The API already answers them in order; the sort keeps
// the screen's own draft list honest while it changes.
const inPosition = (rows) => [...(rows || [])].sort((a, b) => positionOf(a) - positionOf(b));

const versionOf = (row) => (row?.versions || []).find((v) => v.current) || null;

// The one error text the backend sends (a plain string), else the caller's fallback.
const messageOf = (err, intl, id, def) => {
  const data = err?.response?.data;
  if (typeof data === 'string' && data) return data;
  return intl.formatMessage({ id, defaultMessage: def });
};

// The files: one column, the big position badge first, nothing side by side that can grow.
const DeckList = styled.ol`
  list-style: none;
  margin: 0.35rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;
const DeckRow = styled.li`
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
  min-width: 0;
  padding: 0.55rem 0;
  border-top: 1px solid #eef2f7;
  &:first-child { border-top: 0; padding-top: 0; }
`;
const PositionBadge = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.7rem;
  height: 1.7rem;
  background: ${NAVY};
  color: #fff;
  font-size: 0.95rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
`;
const FileBody = styled.div`flex: 1 1 auto; min-width: 0;`;
const FileName = styled.div`
  font-weight: 700;
  color: ${NAVY};
  font-size: 0.88rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const FileNote = styled(PersonMeta)`overflow-wrap: anywhere;`;
const Earlier = styled.ul`list-style: none; margin: 0.25rem 0 0; padding: 0; display: flex; flex-direction: column; gap: 0.15rem;`;
const CheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: #334155;
  input { flex: none; width: 1rem; height: 1rem; margin: 0; accent-color: ${ACCENT}; }
`;
const SpeakerLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.8rem;
  align-items: center;
  padding: 0.3rem 0;
  border-top: 1px solid #eef2f7;
  &:first-child { border-top: 0; }
`;
const SpeakerBody = styled.div`flex: 1 1 12rem; min-width: 0;`;
const StateWord = styled.span`
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(p) => (p.$state === 'MISSING' ? '#991b1b' : p.$state === 'WITHOUT_SLIDES' ? '#475569' : '#166534')};
`;
const OrderWarning = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  align-items: flex-start;
  margin: 0.5rem 0 0.2rem;
  padding: 0.6rem 0.7rem;
  background: #fffbeb;
  border-left: 4px solid #b45309;
  font-size: 0.8rem;
  color: #713f12;
  line-height: 1.5;
`;
const InlineConfirm = styled.div`
  margin-top: 0.45rem;
  padding: 0.5rem 0.6rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-left: 3px solid #dc2626;
  font-size: 0.8rem;
  color: #334155;
  line-height: 1.5;
`;
const AddBlock = styled.div`
  margin-top: 0.55rem;
  padding: 0.7rem;
  background: #fff;
  border: 1px solid #e2e8f0;
`;
const PositionSelect = styled.select`
  display: block;
  width: 100%;
  max-width: 100%;
  padding: 0.4rem 0.45rem;
  border: 1px solid #cbd5e1;
  background: #fff;
  font: inherit;
  font-size: 0.82rem;
`;
const ErrorText = styled(PanelHint)`color: #991b1b;`;
// The buttons of a row wrap inside the side panel instead of running out of it.
const Actions = styled(SpotActions)`flex-wrap: wrap;`;

// `onChanged` is how the panel tells its owner (the program screen) that the program itself moved:
// taking a suggested title overwrites the entry's title, which the Edit dialog and the program list
// show, so without it they would keep printing the old one until the next manual reload.
const SlidesPanel = ({ eventId, entryId, onChanged }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [busy, setBusy] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // The action waiting for a confirmation (title actions and unpublish): {titleId, titleDef, ...,
  // key, task, result}. `null` when nothing is asked.
  const [confirming, setConfirming] = useState(null);

  // The order draft (Change order), the presenter editor of one file, the file to remove after the
  // second click inside its row, and the add form.
  const [arranging, setArranging] = useState(false);
  const [draftOrder, setDraftOrder] = useState([]);
  const [editingPresenters, setEditingPresenters] = useState(null);
  const [presenterDraft, setPresenterDraft] = useState([]);
  const [removingDeck, setRemovingDeck] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addFile, setAddFile] = useState(null);
  const [addPresenters, setAddPresenters] = useState([]);
  const [addPosition, setAddPosition] = useState('');

  // The materials of the event, of which this panel keeps the row of its own entry. Dependencies:
  // `eventId`/`entryId` (given as strings from the route, so the row is matched by number),
  // `reloadKey` (bumped after every write), `intl` (the error texts) and `navigate` (the 401 redirect).
  useEffect(() => {
    if (eventId == null || entryId == null) return undefined;
    let live = true;
    setLoading(true);
    setError(null);
    fetchMaterials(eventId)
      .then((rows) => {
        if (!live) return;
        const wanted = Number(entryId);
        setLoaded((rows || []).find((row) => row.entryId === wanted) || null);
      })
      .catch((err) => {
        if (!live) return;
        if (err.response?.status === 401) { navigate('/admin/login'); return; }
        setError(messageOf(err, intl, 'programAdmin.slides.loadFailed', 'Could not load the talk files.'));
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [eventId, entryId, reloadKey, intl, navigate]);

  // One action at a time: `busy` names the running one, a write bumps `reloadKey` and shows its
  // success line, and every failure lands in the same place. `staleOn409` is for the two order routes:
  // their 409 always means "the list changed", so the panel says so and re-reads the overview.
  const run = async (key, task, {
    successId, successDef, failedId, failedDef, reload = true, changed = false, staleOn409 = false, onStale,
  } = {}) => {
    setBusy(key);
    setActionError(null);
    setSuccess(null);
    try {
      await task();
      if (successId) setSuccess(intl.formatMessage({ id: successId, defaultMessage: successDef }));
      if (reload) setReloadKey((k) => k + 1);
      // A write that moved the program itself (a title taken or dismissed) tells the owner, which
      // re-reads it; a refusal must not, so this stays inside the try.
      if (changed) onChanged?.();
      return true;
    } catch (err) {
      if (err.response?.status === 401) { navigate('/admin/login'); return false; }
      if (staleOn409 && err.response?.status === 409) {
        setActionError(intl.formatMessage({
          id: 'programAdmin.slides.filesChanged',
          defaultMessage: 'The files changed meanwhile. The current state is shown.',
        }));
        setReloadKey((k) => k + 1);
        onStale?.();
        return false;
      }
      setActionError(messageOf(err, intl, failedId, failedDef));
      return false;
    } finally {
      setBusy(null);
    }
  };

  // Confirmed actions: the dialog stays open while the call runs (a refusal keeps it open with the
  // message) and closes once the write went through.
  const confirmed = async () => {
    const spec = confirming;
    if (!spec) return;
    if (await run(spec.key, spec.task, spec.result)) setConfirming(null);
  };

  // The talk read for this entry; a row read for the entry shown before never stands in for it.
  const talk = loaded && loaded.entryId === Number(entryId) ? loaded : null;
  const decks = inPosition(talk ? talk.decks : []);
  const speakers = talk ? (talk.speakers || []) : [];
  const orderedRows = arranging
    ? draftOrder.map((id) => decks.find((row) => deckOf(row).id === id)).filter(Boolean)
    : decks;
  const speakerOf = (assignmentId) => speakers.find((s) => s.assignmentId === assignmentId) || null;
  const personName = (assignmentId) => speakerOf(assignmentId)?.personName || `#${assignmentId}`;
  const disabled = busy !== null;
  const order = talk?.order || {};
  const needsConfirmation = decks.length >= 2 && !!order.needsConfirmation;
  const confirmedOrder = decks.length >= 2 && !order.needsConfirmation && order.confirmedAt && order.confirmedByName;
  const orphanIds = talk?.orphanDeckIds || [];

  const startArranging = () => {
    setDraftOrder(inPosition(decks).map((row) => deckOf(row).id));
    setArranging(true);
    setActionError(null);
    setSuccess(null);
  };

  const moveDraft = (index, direction) => {
    const next = index + direction;
    if (next < 0 || next >= draftOrder.length) return;
    setDraftOrder((list) => {
      const copy = [...list];
      [copy[index], copy[next]] = [copy[next], copy[index]];
      return copy;
    });
  };

  const saveOrder = () => run('order:save', () => reorderDecks(eventId, entryId, draftOrder), {
    successId: 'programAdmin.slides.orderSaved', successDef: 'The order was saved.',
    failedId: 'programAdmin.slides.orderFailed', failedDef: 'Could not save the order.',
    staleOn409: true,
    onStale: () => setArranging(false),
  }).then((ok) => { if (ok) setArranging(false); return ok; });

  const confirmOrder = () => run('order:confirm',
    () => confirmDeckOrder(eventId, entryId, inPosition(decks).map((row) => deckOf(row).id)), {
      successId: 'programAdmin.slides.orderConfirmedSaved', successDef: 'The order was confirmed.',
      failedId: 'programAdmin.slides.orderConfirmFailed', failedDef: 'Could not confirm the order.',
      staleOn409: true,
    });

  const toggleWithoutSlides = (speaker, value) => run(`without:${speaker.assignmentId}`,
    () => setPresentsWithoutSlides(eventId, speaker.assignmentId, value), {
      failedId: 'programAdmin.slides.withoutSlidesFailed', failedDef: 'Could not save the answer.',
    });

  const openPresenters = (row) => {
    setEditingPresenters(deckOf(row).id);
    setPresenterDraft([...(deckOf(row).presenterAssignmentIds || [])]);
    setActionError(null);
    setSuccess(null);
  };

  const savePresenters = (row) => run(`presenters:${deckOf(row).id}`,
    () => setDeckPresenters(eventId, entryId, deckOf(row).id, presenterDraft), {
      successId: 'programAdmin.slides.presentersSaved', successDef: 'The presenters were saved.',
      failedId: 'programAdmin.slides.presentersFailed', failedDef: 'Could not change the presenters.',
    }).then((ok) => { if (ok) setEditingPresenters(null); return ok; });

  const removeFile = (row) => run(`remove:${deckOf(row).id}`, () => removeDeck(eventId, entryId, deckOf(row).id), {
    successId: 'programAdmin.slides.removeSaved', successDef: 'The file was removed.',
    failedId: 'programAdmin.slides.removeFailed', failedDef: 'Could not remove the file.',
  }).then((ok) => { if (ok) setRemovingDeck(null); return ok; });

  const replaceFile = (row, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    run(`replace:${deckOf(row).id}`, () => addDeckVersion(eventId, entryId, deckOf(row).id, file), {
      successId: 'programAdmin.slides.replaced', successDef: 'The new version was uploaded.',
      failedId: 'programAdmin.slides.replaceFailed', failedDef: 'Could not upload the new version.',
    });
  };

  const submitAdd = () => {
    if (!addFile || (decks.length > 0 && addPosition === '') || addPresenters.length === 0) return;
    run('add', () => addDeck(eventId, entryId, addFile, {
      position: decks.length ? Number(addPosition) : null,
      presenterAssignmentIds: addPresenters,
    }), {
      successId: 'programAdmin.slides.added', successDef: 'The file was added.',
      failedId: 'programAdmin.slides.addFailed', failedDef: 'Could not add the file.',
    }).then((ok) => {
      if (ok) {
        setAddOpen(false);
        setAddFile(null);
        setAddPresenters([]);
        setAddPosition('');
      }
    });
  };

  const positionOptions = decks.length === 0 ? [] : [
    { value: 1, label: intl.formatMessage({ id: 'programAdmin.slides.positionBefore', defaultMessage: 'Before 1. {file}' },
      { file: deckOf(decks[0]).fileName || '' }) },
    ...decks.slice(1).map((row, index) => ({
      value: index + 2,
      label: intl.formatMessage({ id: 'programAdmin.slides.positionBetween', defaultMessage: 'Between {before} and {after}' },
        { before: index + 1, after: index + 2 }),
    })),
    { value: decks.length + 1, label: intl.formatMessage({ id: 'programAdmin.slides.positionAfter', defaultMessage: 'After {position}. {file}' },
      { position: decks.length, file: deckOf(decks[decks.length - 1]).fileName || '' }) },
  ];

  return (
    <PanelSection>
      <PanelSectionTitle>
        <FormattedMessage id="programAdmin.slides.title" defaultMessage="Talk materials" />
      </PanelSectionTitle>

      {/* Only the first read shows this; a re-read after a write keeps the talk on screen. */}
      {loading && !talk && (
        <PersonMeta><FormattedMessage id="programAdmin.slides.loading" defaultMessage="Loading the talk materials…" /></PersonMeta>
      )}

      {!loading && error && (
        <>
          <ErrorText>{error}</ErrorText>
          <MiniButton type="button" onClick={() => setReloadKey((k) => k + 1)}>
            <FormattedMessage id="programAdmin.slides.retry" defaultMessage="Retry" />
          </MiniButton>
        </>
      )}

      {!loading && !error && !talk && (
        <PanelHint>
          <FormattedMessage id="programAdmin.slides.none" defaultMessage="No talk materials on this entry." />
        </PanelHint>
      )}

      {!error && talk && (
        <>
          <PanelRow>
            <PanelLabel><FormattedMessage id="programAdmin.slides.asksForSlides" defaultMessage="Asks for slides" /></PanelLabel>
            <span>
              {talk.asksForSlides
                ? <FormattedMessage id="programAdmin.slides.asksForSlidesYes" defaultMessage="Yes" />
                : <FormattedMessage id="programAdmin.slides.asksForSlidesNo" defaultMessage="No" />}
            </span>
          </PanelRow>

          {!talk.asksForSlides && (
            <PanelHint>
              <FormattedMessage id="programAdmin.slides.noSlides"
                defaultMessage="This entry asks for no slides, so there is nothing to upload or publish here. A title a speaker suggested on it is decided below." />
            </PanelHint>
          )}

          {talk.asksForSlides && (
            <>
              {/* Speakers: what is still needed from each, their consent, and staff's own "presents
                  without slides" switch (D6; setting it takes them off the files they present). */}
              <PanelRow>
                <PanelLabel><FormattedMessage id="programAdmin.slides.speakers" defaultMessage="Speakers" /></PanelLabel>
                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                  {speakers.length === 0 && (
                    <PersonMeta><FormattedMessage id="programAdmin.slides.speakersNone" defaultMessage="No confirmed speaker yet." /></PersonMeta>
                  )}
                  {speakers.map((speaker) => (
                    <SpeakerLine key={speaker.assignmentId}>
                      <SpeakerBody>
                        <PersonName>{speaker.personName}</PersonName>
                        <PersonMeta>
                          <StateWord $state={speaker.state}>
                            {speaker.state === 'COVERED'
                              ? <FormattedMessage id="programAdmin.slides.stateCovered" defaultMessage="Covered" />
                              : speaker.state === 'WITHOUT_SLIDES'
                                ? <FormattedMessage id="programAdmin.slides.stateWithoutSlides" defaultMessage="Without slides" />
                                : <FormattedMessage id="programAdmin.slides.stateMissing" defaultMessage="Missing" />}
                          </StateWord>
                          {' · '}
                          <ConsentTag $tone={consentTone(speaker.consent)}>{speaker.consent || 'UNSET'}</ConsentTag>
                          {speaker.consentAt && <>{' '}<HistoryWhen>{formatDateTime(intl, speaker.consentAt)}</HistoryWhen></>}
                        </PersonMeta>
                      </SpeakerBody>
                      <CheckLabel>
                        <input
                          type="checkbox"
                          checked={!!speaker.presentsWithoutSlides}
                          disabled={disabled}
                          onChange={(e) => toggleWithoutSlides(speaker, e.target.checked)}
                        />
                        <FormattedMessage id="programAdmin.slides.withoutSlides" defaultMessage="Presents without slides" />
                      </CheckLabel>
                    </SpeakerLine>
                  ))}
                  <PanelHint>
                    <FormattedMessage id="programAdmin.slides.withoutSlidesHint"
                      defaultMessage="Setting it takes the speaker off the files they present." />
                  </PanelHint>
                </div>
              </PanelRow>

              {/* The order: unmistakable while it needs a confirming click, quiet once confirmed. */}
              {decks.length >= 2 && (
                <PanelRow>
                  <PanelLabel><FormattedMessage id="programAdmin.slides.order" defaultMessage="Order" /></PanelLabel>
                  <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                    {arranging ? (
                      <Actions>
                        <MiniButton type="button" disabled={disabled} onClick={saveOrder}>
                          <FormattedMessage id="programAdmin.slides.saveOrder" defaultMessage="Save order" />
                        </MiniButton>
                        <MiniButton type="button" disabled={disabled}
                          onClick={() => { setArranging(false); setActionError(null); }}>
                          <FormattedMessage id="programAdmin.slides.cancel" defaultMessage="Cancel" />
                        </MiniButton>
                      </Actions>
                    ) : (
                      <Actions>
                        <MiniButton type="button" disabled={disabled} onClick={startArranging}>
                          <FormattedMessage id="programAdmin.slides.changeOrder" defaultMessage="Change order" />
                        </MiniButton>
                      </Actions>
                    )}
                    {needsConfirmation && !arranging && (
                      <OrderWarning>
                        <strong><FormattedMessage id="programAdmin.slides.orderNotConfirmed" defaultMessage="Order not confirmed yet" /></strong>
                        <span>
                          <FormattedMessage id="programAdmin.slides.orderWhy"
                            defaultMessage="The public program shows the files in this order." />
                        </span>
                        <MiniButton type="button" disabled={disabled} onClick={confirmOrder}>
                          <FormattedMessage id="programAdmin.slides.confirmOrder" defaultMessage="Confirm order" />
                        </MiniButton>
                      </OrderWarning>
                    )}
                    {confirmedOrder && (
                      <PanelHint>
                        <FormattedMessage id="programAdmin.slides.orderConfirmed"
                          defaultMessage="Order confirmed by {name} on {date}."
                          values={{ name: order.confirmedByName, date: formatDateTime(intl, order.confirmedAt) }} />
                      </PanelHint>
                    )}
                  </div>
                </PanelRow>
              )}

              <PanelRow>
                <PanelLabel><FormattedMessage id="programAdmin.slides.files" defaultMessage="Files" /></PanelLabel>
                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                  {decks.length === 0 && (
                    <PanelHint><FormattedMessage id="programAdmin.slides.noFiles" defaultMessage="No file uploaded yet." /></PanelHint>
                  )}

                  {orderedRows.length > 0 && (
                    <DeckList>
                      {orderedRows.map((row, index) => {
                        const deck = deckOf(row);
                        const versions = row.versions || [];
                        const current = versionOf(row);
                        const earlier = versions.filter((v) => v !== current);
                        const presenterIds = deck.presenterAssignmentIds || [];
                        const orphan = presenterIds.length === 0 || orphanIds.includes(deck.id);
                        const blockers = nonEmpty(row.blockers);
                        return (
                          <DeckRow key={deck.id}>
                            <PositionBadge data-testid="deck-position">{arranging ? index + 1 : deck.position}</PositionBadge>
                            <FileBody>
                              <FileName title={deck.fileName || undefined}>
                                {deck.fileName || <FormattedMessage id="programAdmin.slides.noVersion" defaultMessage="File without a version" />}
                              </FileName>
                              <FileNote>
                                {orphan
                                  ? <FormattedMessage id="programAdmin.slides.nobodyPresents" defaultMessage="Nobody presents this file" />
                                  : <FormattedMessage id="programAdmin.slides.presentedBy" defaultMessage="Presented by {names}"
                                    values={{ names: presenterIds.map(personName).join(', ') }} />}
                              </FileNote>
                              {current && (
                                <FileNote>
                                  {formatSize(current.sizeBytes)}
                                  {current.uploadedByName && <> · <FormattedMessage id="programAdmin.slides.uploadedBy"
                                    defaultMessage="uploaded by {name}" values={{ name: current.uploadedByName }} /></>}
                                  {current.uploadedAt && <> · <FormattedMessage id="programAdmin.slides.uploadedOn"
                                    defaultMessage="uploaded {date}" values={{ date: formatDateTime(intl, current.uploadedAt) }} /></>}
                                  {current.reviewedAt && <> · <FormattedMessage id="programAdmin.slides.reviewedAt"
                                    defaultMessage="reviewed {date}" values={{ date: formatDateTime(intl, current.reviewedAt) }} /></>}
                                  {row.publishedAt && <> · <FormattedMessage id="programAdmin.slides.publishedAt"
                                    defaultMessage="published {date}" values={{ date: formatDateTime(intl, row.publishedAt) }} /></>}
                                </FileNote>
                              )}

                              {blockers.map((blocker) => (
                                <FileNote key={blocker} style={{ color: '#991b1b' }}>{blocker}</FileNote>
                              ))}

                              {!arranging && (
                                <Actions style={{ marginTop: '.35rem' }}>
                                  {current && (
                                    <MiniButton type="button" disabled={disabled}
                                      onClick={() => run(`download:${current.id}`,
                                        () => downloadMaterial(eventId, current.id, current.fileName),
                                        { failedId: 'programAdmin.slides.downloadFailed', failedDef: 'Could not download the file.', reload: false })}>
                                      <FormattedMessage id="programAdmin.slides.download" defaultMessage="Download" />
                                    </MiniButton>
                                  )}
                                  {current && (
                                    <MiniButton type="button" disabled={disabled}
                                      onClick={() => run(`review:${current.id}`,
                                        () => reviewMaterial(eventId, current.id), {
                                          successId: 'programAdmin.slides.reviewed', successDef: 'The upload was marked as reviewed.',
                                          failedId: 'programAdmin.slides.reviewFailed', failedDef: 'Could not mark the upload as reviewed.',
                                        })}>
                                      <FormattedMessage id="programAdmin.slides.review" defaultMessage="Review" />
                                    </MiniButton>
                                  )}
                                  {deck.published ? (
                                    <MiniButton type="button" disabled={disabled}
                                      onClick={() => setConfirming({
                                        titleId: 'programAdmin.slides.unpublishConfirmTitle', titleDef: 'Unpublish this file?',
                                        introId: 'programAdmin.slides.unpublishConfirmIntro',
                                        introDef: 'The file stops being downloadable from the public program. The file and its history stay.',
                                        key: `unpublish:${deck.id}`,
                                        task: () => unpublishMaterial(eventId, deck.id),
                                        result: {
                                          successId: 'programAdmin.slides.unpublished', successDef: 'The file was unpublished.',
                                          failedId: 'programAdmin.slides.unpublishFailed', failedDef: 'Could not unpublish the file.',
                                        },
                                      })}>
                                      <FormattedMessage id="programAdmin.slides.unpublish" defaultMessage="Unpublish" />
                                    </MiniButton>
                                  ) : (
                                    <MiniButton type="button" disabled={disabled || blockers.length > 0}
                                      title={blockers.length > 0 ? blockers.join('\n') : undefined}
                                      onClick={() => run(`publish:${deck.id}`,
                                        () => publishMaterial(eventId, deck.id), {
                                          successId: 'programAdmin.slides.published', successDef: 'The file was published.',
                                          failedId: 'programAdmin.slides.publishFailed', failedDef: 'Could not publish the file.',
                                        })}>
                                      <FormattedMessage id="programAdmin.slides.publish" defaultMessage="Publish" />
                                    </MiniButton>
                                  )}
                                  <label style={{ display: 'inline-flex' }}>
                                    <MiniButton as="span">
                                      <FormattedMessage id="programAdmin.slides.replace" defaultMessage="Replace" />
                                    </MiniButton>
                                    <input
                                      type="file"
                                      accept=".ppt,.pptx"
                                      style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0, overflow: 'hidden' }}
                                      aria-label={intl.formatMessage({
                                        id: 'programAdmin.slides.replaceFile', defaultMessage: 'Replace file {position}',
                                      }, { position: deck.position })}
                                      disabled={disabled}
                                      onChange={(e) => replaceFile(row, e)}
                                    />
                                  </label>
                                  <MiniButton type="button" disabled={disabled} onClick={() => openPresenters(row)}>
                                    <FormattedMessage id="programAdmin.slides.whoPresents" defaultMessage="Who presents" />
                                  </MiniButton>
                                  <MiniButton type="button" $danger disabled={disabled} onClick={() => setRemovingDeck(deck.id)}>
                                    <FormattedMessage id="programAdmin.slides.remove" defaultMessage="Remove" />
                                  </MiniButton>
                                </Actions>
                              )}

                              {arranging && (
                                <Actions style={{ marginTop: '.35rem' }}>
                                  <MiniButton type="button" disabled={disabled || index === 0}
                                    aria-label={intl.formatMessage({ id: 'programAdmin.slides.moveUp', defaultMessage: 'Move up' })}
                                    onClick={() => moveDraft(index, -1)}>
                                    <ArrowUp size={12} aria-hidden="true" />
                                  </MiniButton>
                                  <MiniButton type="button" disabled={disabled || index === orderedRows.length - 1}
                                    aria-label={intl.formatMessage({ id: 'programAdmin.slides.moveDown', defaultMessage: 'Move down' })}
                                    onClick={() => moveDraft(index, 1)}>
                                    <ArrowDown size={12} aria-hidden="true" />
                                  </MiniButton>
                                </Actions>
                              )}

                              {editingPresenters === deck.id && (
                                <div style={{ marginTop: '.45rem', padding: '.55rem', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                                  <PersonMeta><FormattedMessage id="programAdmin.slides.presentersLabel" defaultMessage="Who presents this file" /></PersonMeta>
                                  {speakers.map((speaker) => (
                                    <CheckLabel key={speaker.assignmentId}>
                                      <input
                                        type="checkbox"
                                        checked={presenterDraft.includes(speaker.assignmentId)}
                                        disabled={disabled}
                                        onChange={(e) => setPresenterDraft((list) => (e.target.checked
                                          ? [...list, speaker.assignmentId]
                                          : list.filter((id) => id !== speaker.assignmentId)))}
                                      />
                                      {speaker.personName}
                                    </CheckLabel>
                                  ))}
                                  {presenterDraft.length === 0 && (
                                    <ErrorText><FormattedMessage id="programAdmin.slides.presentersRequired"
                                      defaultMessage="Choose at least one person." /></ErrorText>
                                  )}
                                  <Actions style={{ marginTop: '.35rem' }}>
                                    <MiniButton type="button" disabled={disabled || presenterDraft.length === 0}
                                      onClick={() => savePresenters(row)}>
                                      <FormattedMessage id="programAdmin.slides.savePresenters" defaultMessage="Save presenters" />
                                    </MiniButton>
                                    <MiniButton type="button" disabled={disabled}
                                      onClick={() => setEditingPresenters(null)}>
                                      <FormattedMessage id="programAdmin.slides.cancel" defaultMessage="Cancel" />
                                    </MiniButton>
                                  </Actions>
                                </div>
                              )}

                              {earlier.length > 0 && (
                                <div style={{ marginTop: '.35rem' }}>
                                  <PersonMeta><FormattedMessage id="programAdmin.slides.earlierVersions" defaultMessage="Earlier versions" /></PersonMeta>
                                  <Earlier>
                                    {earlier.map((version) => (
                                      <li key={version.id}>
                                        <MiniButton type="button" disabled={disabled}
                                          onClick={() => run(`download:${version.id}`,
                                            () => downloadMaterial(eventId, version.id, version.fileName),
                                            { failedId: 'programAdmin.slides.downloadFailed', failedDef: 'Could not download the file.', reload: false })}>
                                          <FormattedMessage id="programAdmin.slides.downloadVersion" defaultMessage="Download version {version}"
                                            values={{ version: version.fileName }} />
                                        </MiniButton>
                                        <PersonMeta as="span" style={{ marginLeft: '.4rem' }}>
                                          {formatDateTime(intl, version.uploadedAt)}
                                          {version.reviewedAt && <> · <FormattedMessage id="programAdmin.slides.reviewedAt"
                                            defaultMessage="reviewed {date}" values={{ date: formatDateTime(intl, version.reviewedAt) }} /></>}
                                        </PersonMeta>
                                      </li>
                                    ))}
                                  </Earlier>
                                </div>
                              )}

                              {removingDeck === deck.id && (
                                <InlineConfirm>
                                  <strong><FormattedMessage id="programAdmin.slides.removeConfirmTitle" defaultMessage="Remove this file?" /></strong>
                                  {' '}
                                  <FormattedMessage id="programAdmin.slides.removeConfirmBody"
                                    defaultMessage="All versions of this file are deleted." />
                                  <Actions style={{ marginTop: '.35rem' }}>
                                    <MiniButton type="button" $danger disabled={disabled} onClick={() => removeFile(row)}>
                                      <FormattedMessage id="programAdmin.slides.removeConfirm" defaultMessage="Remove file" />
                                    </MiniButton>
                                    <MiniButton type="button" disabled={disabled} onClick={() => setRemovingDeck(null)}>
                                      <FormattedMessage id="programAdmin.slides.cancel" defaultMessage="Cancel" />
                                    </MiniButton>
                                  </Actions>
                                </InlineConfirm>
                              )}
                            </FileBody>
                          </DeckRow>
                        );
                      })}
                    </DeckList>
                  )}

                  {!arranging && (
                    <Actions style={{ marginTop: '.5rem' }}>
                      <MiniButton type="button" disabled={disabled} onClick={() => setAddOpen((open) => !open)}>
                        <FormattedMessage id="programAdmin.slides.addFile" defaultMessage="Add a file" />
                      </MiniButton>
                      {decks.length > 0 && (
                        <MiniButton type="button" disabled={disabled}
                          onClick={() => run('zip', () => downloadDecksZip(eventId, entryId), {
                            successId: 'programAdmin.slides.zipStarted', successDef: 'The download started.',
                            failedId: 'programAdmin.slides.zipFailed', failedDef: 'Could not download the files.',
                            reload: false,
                          })}>
                          <FormattedMessage id="programAdmin.slides.downloadZip" defaultMessage="Download all (zip)" />
                        </MiniButton>
                      )}
                    </Actions>
                  )}

                  {addOpen && !arranging && (
                    <AddBlock>
                      <PersonMeta>
                        <FormattedMessage id="programAdmin.slides.addRule"
                          defaultMessage="If there is more than one file, they are shown in the order the speakers present them." />
                      </PersonMeta>
                      <input
                        type="file"
                        accept=".ppt,.pptx"
                        aria-label={intl.formatMessage({ id: 'programAdmin.slides.chooseFile', defaultMessage: 'File to add' })}
                        disabled={disabled}
                        onChange={(e) => setAddFile(e.target.files?.[0] || null)}
                      />
                      {decks.length > 0 && (
                        <div style={{ marginTop: '.4rem' }}>
                          <PersonMeta><FormattedMessage id="programAdmin.slides.positionLabel" defaultMessage="Where does it go?" /></PersonMeta>
                          <PositionSelect
                            aria-label={intl.formatMessage({ id: 'programAdmin.slides.positionLabel', defaultMessage: 'Where does it go?' })}
                            value={addPosition}
                            disabled={disabled}
                            onChange={(e) => setAddPosition(e.target.value)}
                          >
                            <option value="">
                              {intl.formatMessage({ id: 'programAdmin.slides.positionChoose', defaultMessage: 'Choose where this file goes…' })}
                            </option>
                            {positionOptions.map((option) => (
                              <option key={option.value} value={String(option.value)}>{option.label}</option>
                            ))}
                          </PositionSelect>
                        </div>
                      )}
                      <div style={{ marginTop: '.4rem' }}>
                        <PersonMeta><FormattedMessage id="programAdmin.slides.whoPresents" defaultMessage="Who presents" /></PersonMeta>
                        {speakers.map((speaker) => (
                          <CheckLabel key={speaker.assignmentId}>
                            <input
                              type="checkbox"
                              checked={addPresenters.includes(speaker.assignmentId)}
                              disabled={disabled}
                              onChange={(e) => setAddPresenters((list) => (e.target.checked
                                ? [...list, speaker.assignmentId]
                                : list.filter((id) => id !== speaker.assignmentId)))}
                            />
                            {speaker.personName}
                          </CheckLabel>
                        ))}
                        {addPresenters.length === 0 && (
                          <ErrorText><FormattedMessage id="programAdmin.slides.presentersRequired"
                            defaultMessage="Choose at least one person." /></ErrorText>
                        )}
                      </div>
                      <Actions style={{ marginTop: '.45rem' }}>
                        <MiniButton type="button"
                          disabled={disabled || !addFile || (decks.length > 0 && addPosition === '') || addPresenters.length === 0}
                          onClick={submitAdd}>
                          <FormattedMessage id="programAdmin.slides.addSubmit" defaultMessage="Add file" />
                        </MiniButton>
                        <MiniButton type="button" disabled={disabled}
                          onClick={() => { setAddOpen(false); setAddFile(null); setAddPresenters([]); setAddPosition(''); }}>
                          <FormattedMessage id="programAdmin.slides.cancel" defaultMessage="Cancel" />
                        </MiniButton>
                      </Actions>
                    </AddBlock>
                  )}
                </div>
              </PanelRow>
            </>
          )}

          {/* The one thing to decide on an entry that asks for no slides: the title a speaker
              suggested on it (owner decision 2026-10-14). */}
          {talk.titleSuggestion && (
            <PanelRow>
              <PanelLabel><FormattedMessage id="programAdmin.slides.suggestion" defaultMessage="Suggested title" /></PanelLabel>
              <div>
                <PersonName>{talk.titleSuggestion}</PersonName>
                <PersonMeta>
                  <FormattedMessage id="programAdmin.slides.suggestedBy" defaultMessage="Suggested by #{personId}{when}"
                    values={{
                      personId: talk.titleSuggestedByPersonId,
                      when: talk.titleSuggestedAt ? <> — <HistoryWhen>{formatDateTime(intl, talk.titleSuggestedAt)}</HistoryWhen></> : '',
                    }} />
                </PersonMeta>
                <Actions style={{ marginTop: '.35rem' }}>
                  {['EN', 'DE'].map((language) => (
                    <MiniButton key={language} type="button" disabled={disabled}
                      onClick={() => setConfirming({
                        titleId: 'programAdmin.slides.acceptConfirmTitle', titleDef: 'Replace the entry title?',
                        introId: 'programAdmin.slides.acceptConfirmIntro',
                        introDef: 'The entry’s {language} title is overwritten with what the speaker suggested, and the change appears in the program at once.',
                        introValues: { language },
                        key: `accept:${language}`,
                        task: () => acceptTitleSuggestion(eventId, entryId, language),
                        result: {
                          successId: 'programAdmin.slides.suggestionAccepted', successDef: 'The suggested title was taken.',
                          failedId: 'programAdmin.slides.suggestionAcceptFailed', failedDef: 'Could not take the suggested title.',
                          changed: true,
                        },
                      })}>
                      <FormattedMessage id="programAdmin.slides.accept" defaultMessage="Accept {language}" values={{ language }} />
                    </MiniButton>
                  ))}
                  <MiniButton type="button" disabled={disabled}
                    onClick={() => setConfirming({
                      titleId: 'programAdmin.slides.dismissConfirmTitle', titleDef: 'Dismiss this suggestion?',
                      introId: 'programAdmin.slides.dismissConfirmIntro',
                      introDef: 'The suggested title is dropped; the entry keeps the organizer’s own title. The speaker is not told.',
                      key: 'dismiss',
                      task: () => dismissTitleSuggestion(eventId, entryId),
                      result: {
                        successId: 'programAdmin.slides.suggestionDismissed', successDef: 'The suggested title was dismissed.',
                        failedId: 'programAdmin.slides.suggestionDismissFailed', failedDef: 'Could not dismiss the suggested title.',
                        changed: true,
                      },
                    })}>
                    <FormattedMessage id="programAdmin.slides.dismiss" defaultMessage="Dismiss" />
                  </MiniButton>
                </Actions>
              </div>
            </PanelRow>
          )}
        </>
      )}

      {actionError && !confirming && <ErrorText>{actionError}</ErrorText>}
      {success && <PanelHint>{success}</PanelHint>}

      <ConfirmDialog
        open={!!confirming}
        tone="warning"
        title={confirming ? intl.formatMessage({ id: confirming.titleId, defaultMessage: confirming.titleDef }) : ''}
        intro={confirming ? intl.formatMessage({ id: confirming.introId, defaultMessage: confirming.introDef }, confirming.introValues) : ''}
        points={confirming?.points || []}
        confirmLabel={confirming ? intl.formatMessage({ id: confirming.confirmId || 'programAdmin.confirm.confirm', defaultMessage: confirming.confirmDef || 'Confirm' }) : ''}
        cancelLabel={intl.formatMessage({ id: 'programAdmin.confirm.cancel', defaultMessage: 'Cancel' })}
        error={actionError}
        busy={busy !== null}
        onConfirm={confirmed}
        onClose={() => { setConfirming(null); setActionError(null); }}
      />
    </PanelSection>
  );
};

export default SlidesPanel;
